// Mirrors how the bulletin search API (`bulletin_search_clause` in api/bulletin_utils.py)
// reads a query, so highlights land on what the search actually matched.

export interface ParsedSearchQuery {
  /** Highlighted as a whole, as an exact match. */
  phrases: string[];
  /** Highlighted as whole words. */
  terms: string[];
  /** Highlighted at the start of any word. */
  prefix: string | null;
}

export interface HighlightPart {
  text: string;
  type: "exact" | "partial" | "normal";
}

// Quotes, a standalone `or` and a leading `-` switch the API to web-search parsing.
const WEBSEARCH_SYNTAX = /"|(^|\s)-\S|(^|\s)or(\s|$)/i;
const WEBSEARCH_TOKEN = /(-?)"([^"]*)"?|(\S+)/g;

const WORD_CHAR = "[\\p{L}\\p{N}]";
// The left boundary is consumed rather than asserted: RegExp lookbehind breaks
// Safari before 16.4. `splitHighlights` hands the consumed `lead` back as plain text.
const WORD_START = `(?<lead>^|[^\\p{L}\\p{N}])`;
const WORD_END = `(?!${WORD_CHAR})`;

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Postgres splits words on punctuation too, so `red,car` searches `red` and `car`.
function words(value: string): string[] {
  return value.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
}

export function parseSearchQuery(
  searchText: string | null | undefined,
): ParsedSearchQuery {
  const query = searchText?.trim() ?? "";

  if (!WEBSEARCH_SYNTAX.test(query)) {
    // Plain input: any word matches, the whole input ranks first as a phrase,
    // and the last word is a prefix so results follow typing.
    const all = words(query);
    return {
      phrases: all.length > 0 ? [all.join(" ")] : [],
      terms: all.slice(0, -1),
      prefix: all[all.length - 1] ?? null,
    };
  }

  const phrases: string[] = [];
  const terms: string[] = [];
  for (const [, negated, quoted, bare] of query.matchAll(WEBSEARCH_TOKEN)) {
    if (quoted !== undefined) {
      const phrase = words(quoted);
      if (!negated && phrase.length > 0) phrases.push(phrase.join(" "));
    } else if (bare.toLowerCase() !== "or" && !/^-./.test(bare)) {
      terms.push(...words(bare));
    }
  }
  return { phrases, terms, prefix: null };
}

/** Split `text` into the runs that `searchText` matches and the runs it does not. */
export function splitHighlights(
  text: string,
  searchText: string | null | undefined,
): HighlightPart[] {
  const { phrases, terms, prefix } = parseSearchQuery(searchText);

  const exact = phrases.map((phrase) =>
    phrase.split(" ").map(escapeRegExp).join(`[^\\p{L}\\p{N}]+`),
  );
  const partial = terms.map(escapeRegExp);
  if (prefix) partial.push(`${escapeRegExp(prefix)}${WORD_CHAR}*`);

  const alternatives = [
    exact.length > 0 && `(?<exact>${exact.join("|")})${WORD_END}`,
    partial.length > 0 && `(?<partial>${partial.join("|")})${WORD_END}`,
  ].filter(Boolean);
  if (alternatives.length === 0) return [{ text, type: "normal" }];

  const pattern = new RegExp(
    `${WORD_START}(?:${alternatives.join("|")})`,
    "giu",
  );

  const parts: HighlightPart[] = [];
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const lead = match.groups?.lead ?? "";
    const start = (match.index ?? 0) + lead.length;
    const matched = match[0].slice(lead.length);
    if (start > last)
      parts.push({ text: text.slice(last, start), type: "normal" });
    parts.push({
      text: matched,
      type: match.groups?.exact !== undefined ? "exact" : "partial",
    });
    last = start + matched.length;
  }
  if (last < text.length)
    parts.push({ text: text.slice(last), type: "normal" });
  return parts;
}
